import React, { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { getNotifications, markRead, markAllRead } from '@/lib/api/notifications'
import type { Notification } from '@/lib/types'

const TYPE_ICONS: Record<string, string> = {
  case_update:     '📋',
  appointment:     '📅',
  inventory_low:   '⚠️',
  volunteer_hours: '⏱️',
  partner_request: '🤝',
  document_upload: '📎',
  system:          '🔔',
  announcement:    '📣',
}

// NotificationItem defined at module scope — stable identity
const NotificationItem = React.memo(({
  notif, onClick
}: { notif: Notification; onClick: (notif: Notification) => void }) => (
  <button
    onClick={() => onClick(notif)}
    className={[
      'w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-800',
      'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors',
      !notif.is_read ? 'bg-brand-50 dark:bg-brand-950' : '',
    ].join(' ')}
  >
    <div className="flex gap-3 items-start">
      <span className="text-lg flex-shrink-0 mt-0.5">
        {TYPE_ICONS[notif.type] ?? '🔔'}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-sm ${!notif.is_read ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
            {notif.title}
          </p>
          {!notif.is_read && (
            <span className="w-2 h-2 rounded-full bg-brand-500 flex-shrink-0" />
          )}
        </div>
        {notif.body && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.body}</p>
        )}
        <p className="text-xs text-gray-400 mt-1">
          {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
        </p>
      </div>
    </div>
  </button>
))
NotificationItem.displayName = 'NotificationItem'

interface NotificationCenterProps {
  open: boolean
  onClose: () => void
}

// Warm W.E.E. chime: layered bell partials with a soft, rounded decay.
// AudioContext.resume() is awaited because browsers can suspend audio until a user gesture.
const playWEEAlertSound = async () => {
  let ctx: AudioContext | null = null
  try {
    const AudioCtx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtx) return
    ctx = new AudioCtx()
    await ctx.resume()
    const notes = [
      { frequency: 659.25, at: 0.00 },
      { frequency: 830.61, at: 0.17 },
      { frequency: 987.77, at: 0.34 },
      { frequency: 1318.51, at: 0.55 },
      { frequency: 1046.50, at: 0.78 },
    ]
    const startAt = ctx.currentTime + 0.03
    notes.forEach(({ frequency, at }) => {
      const noteStart = startAt + at
      // Two softly detuned partials create a bell-like shimmer rather than an electronic beep.
      ;[1, 2.756].forEach((partial, partialIndex) => {
        const oscillator = ctx!.createOscillator()
        const gain = ctx!.createGain()
        const voice = ctx!.createGain()
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(frequency * partial, noteStart)
        voice.gain.value = partialIndex === 0 ? 0.72 : 0.16
        gain.gain.setValueAtTime(0.0001, noteStart)
        gain.gain.exponentialRampToValueAtTime(partialIndex === 0 ? 0.13 : 0.045, noteStart + 0.012)
        gain.gain.exponentialRampToValueAtTime(0.0001, noteStart + (partialIndex === 0 ? 0.48 : 0.32))
        oscillator.connect(gain)
        gain.connect(voice)
        voice.connect(ctx!.destination)
        oscillator.start(noteStart)
        oscillator.stop(noteStart + 0.52)
      })
    })
    window.setTimeout(() => { if (ctx && ctx.state !== 'closed') void ctx.close() }, 1800)
  } catch {
    if (ctx && ctx.state !== 'closed') void ctx.close()
  }
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ open, onClose }) => {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [soundEnabled, setSoundEnabled] = React.useState(() => {
    try { return window.localStorage.getItem('wee-notification-sound') !== 'off' } catch { return true }
  })
  const toggleSound = useCallback(() => {
    const next = !soundEnabled
    setSoundEnabled(next)
    try { window.localStorage.setItem('wee-notification-sound', next ? 'on' : 'off') } catch { /* device storage may be unavailable */ }
    if (next) void playWEEAlertSound()
  }, [soundEnabled])

  const { data: notifications = [], isLoading, refetch } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn:  () => user ? getNotifications(user.id) : Promise.resolve([]),
    enabled:  !!user,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  })

  // Opening the notification drawer is itself a refresh point. The 10-second
  // fallback below also protects the bell when a Realtime websocket is delayed
  // or unavailable in the browser.
  useEffect(() => {
    if (open && user?.id) void refetch()
  }, [open, user?.id, refetch])

  // Real-time subscription: announcements are inserted after the Settings
  // form submits, while read state is updated separately. Listen to both so
  // the drawer/badge never depends on a hard reload to become current.
  useEffect(() => {
    if (!user?.id) return

    const handleInsert = () => {
      if (soundEnabled) void playWEEAlertSound()
      void qc.invalidateQueries({ queryKey: ['notifications', user.id] })
      void qc.invalidateQueries({ queryKey: ['notif-unread', user.id] })
    }
    const handleUpdate = () => {
      void qc.invalidateQueries({ queryKey: ['notifications', user.id] })
      void qc.invalidateQueries({ queryKey: ['notif-unread', user.id] })
    }
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `recipient_id=eq.${user.id}` },
        handleInsert
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `recipient_id=eq.${user.id}` },
        handleUpdate
      )
      .subscribe()
    return () => { void supabase.removeChannel(channel) }
  }, [user?.id, qc, soundEnabled])

  const readMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) => markRead(id, userId),
    onMutate: async ({ id }: { id: string; userId: string }) => {
      await qc.cancelQueries({ queryKey: ['notifications', user?.id] })
      const previous = qc.getQueryData<Notification[]>(['notifications', user?.id])
      qc.setQueryData(
        ['notifications', user?.id],
        (old: Notification[] | undefined) => (old ?? []).map(n => n.id === id ? { ...n, is_read: true } : n)
      )
      return { previous }
    },
    onError: (_error, _id, context) => {
      if (context?.previous) qc.setQueryData(['notifications', user?.id], context.previous)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['notifications', user?.id] })
      qc.invalidateQueries({ queryKey: ['notif-unread', user?.id] })
    },
  })

  const readAllMutation = useMutation({
    mutationFn: () => user ? markAllRead(user.id) : Promise.resolve(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ['notifications', user?.id] })
      const previous = qc.getQueryData<Notification[]>(['notifications', user?.id])
      // Optimistic UI: the unread highlight and count disappear immediately.
      qc.setQueryData(
        ['notifications', user?.id],
        (old: Notification[] | undefined) => (old ?? []).map(n => ({ ...n, is_read: true }))
      )
      qc.setQueryData(['notif-unread', user?.id], 0)
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(['notifications', user?.id], context.previous)
        qc.setQueryData(
          ['notif-unread', user?.id],
          (context.previous ?? []).filter(n => !n.is_read).length
        )
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['notifications', user?.id] })
      qc.invalidateQueries({ queryKey: ['notif-unread', user?.id] })
    },
  })

  const navigate   = useNavigate()
  const handleClick = useCallback((notif: Notification) => {
    if (!notif.is_read && user?.id) readMutation.mutate({ id: notif.id, userId: user.id })
    if (notif.link) { navigate(notif.link); onClose() }
  }, [readMutation, navigate, onClose])
  const handleReadAll = useCallback(() => readAllMutation.mutate(), [readAllMutation])

  const unreadCount = notifications.filter(n => !n.is_read).length

  return (
    <AnimatePresence>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="bg-brand-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
              <button type="button" onClick={toggleSound} className="text-xs font-semibold text-brand-600 hover:text-brand-700" title={soundEnabled ? 'Turn notification sound off on this device' : 'Turn notification sound on and test it'} aria-pressed={soundEnabled}>{soundEnabled ? '🔔 Sound on' : '🔕 Sound off'}</button>
              {unreadCount > 0 && (
                <button
                  onClick={handleReadAll}
                  className="text-xs text-brand-500 hover:text-brand-600 font-medium"
                >
                  Mark all read
                </button>
              )}
              </div>
            </div>

            {/* List */}
            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="p-8 flex justify-center">
                  <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">
                  <p className="text-3xl mb-2">🔔</p>
                  <p>No notifications yet</p>
                </div>
              ) : (
                notifications.map(n => (
                  <NotificationItem key={n.id} notif={n} onClick={handleClick} />
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default NotificationCenter
